import AsyncTasksStorage from '@/services/AsyncTasksStorage'
import { AsyncTask, AsyncTaskStatus, AsyncTaskType } from '@/types/catalogs/AsyncTask'
import userInfoStore from './userInfo'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import { TIME } from '@/types/GeneralConstants'

export default {
  namespaced: true,
  state: {
    notifications: AsyncTasksStorage
  },
  getters: {
    isDatafeedTaskInProcess (state: any) {
      return state.notifications.getUserTasksByState(AsyncTaskStatus.PROCESSING, userInfoStore.userInfo.id).tasks
        .some((task: { id: number, type: AsyncTaskType, updateDate: string }) => task.type === AsyncTaskType.DATAFEED)
    },
    processingTasks (state: any) {
      return state.notifications.getUserTasksByState(AsyncTaskStatus.PROCESSING, userInfoStore.userInfo.id).tasks
    },
    doneTasks (state: any) {
      return state.notifications.getUserTasksByState(AsyncTaskStatus.DONE, userInfoStore.userInfo.id).tasks
    },
    errorTasks (state: any) {
      return state.notifications.getUserTasksByState(AsyncTaskStatus.ERROR, userInfoStore.userInfo.id).tasks
    },
    canceledTasks (state: any) {
      return state.notifications.getUserTasksByState(AsyncTaskStatus.CANCELED, userInfoStore.userInfo.id).tasks
    },
    blacklistTasks (state: any) {
      return state.notifications.getUserTasksByState(AsyncTaskStatus.BLACKLIST, userInfoStore.userInfo.id).tasks
    }
  },
  mutations: {
    addTask (state: any, payload: { taskState: AsyncTaskStatus, task: { id: number, type: AsyncTaskType, updateDate: string }}) {
      state.notifications.addTaskToUserByState(payload.taskState, userInfoStore.userInfo.id, payload.task)
    },
    removeTask (state: any, payload: { taskState: AsyncTaskStatus, taskId: number }) {
      state.notifications.removeUserTaskByState(payload.taskState, userInfoStore.userInfo.id, payload.taskId)
    }
  },
  actions: {
    async addTaskAsync (context: any, payload: { taskState: AsyncTaskStatus, task: { id: number, type: AsyncTaskType, updateDate: string }}) {
      return new Promise((resolve, reject) => {
        context.commit('addTask', payload)
        resolve(payload.task.id)
      })
    },
    async removeTaskAsync (context: any, payload: { taskState: AsyncTaskStatus, taskId: number }) {
      return new Promise((resolve, reject) => {
        context.commit('removeTask', payload)
        resolve(payload.taskId)
      })
    },
    async reallocateProcessingTasks ({ commit, dispatch, getters }: any) {
      const tasks = [...getters.processingTasks]
      if (tasks.length === 0) return { inProgress: [], reallocated: [] }

      const promises = []
      for (const task of tasks) {
        const request = CatalogsFillsService.getAsyncTaskById(task.id)
        promises.push(request)
      }
      return Promise.allSettled(promises)
        .then((taskPromises) => {
          const tasksStillInProgress: AsyncTask[] = []
          const reallocatedTasks: AsyncTask[] = []
          const processingStates = [AsyncTaskStatus.NEW, AsyncTaskStatus.WAITING, AsyncTaskStatus.PROCESSING]
          for (const taskPromise of taskPromises) {
            let task
            if (taskPromise.status === 'fulfilled') {
              task = taskPromise.value
            }
            if (task !== undefined && !processingStates.includes(task.status)) {
              // This tasks can change only to DONE, ERROR, CANCELED
              reallocatedTasks.push(AsyncTask.createInstance(task))
              dispatch('addTaskAsync', {
                taskState: task.status,
                task: { id: task.id, type: task.type, updateDate: task.updateDate }
              }).then((taskId: number) => {
                commit('removeTask', {
                  taskState: AsyncTaskStatus.PROCESSING,
                  taskId: taskId
                })
              })
            } else if (task !== undefined && processingStates.includes(task.status)) {
              tasksStillInProgress.push(AsyncTask.createInstance(task))
            }
          }
          return { inProgress: tasksStillInProgress, reallocated: reallocatedTasks }
        })
    },
    async cancelTask ({ commit, dispatch }: any, payload: { taskStateFrom: AsyncTaskStatus, task: { id: number, type: AsyncTaskType, updateDate: string } }) {
      dispatch('addTaskAsync', {
        taskState: AsyncTaskStatus.CANCELED,
        task: { id: payload.task.id, type: payload.task.type, updateDate: payload.task.updateDate }
      }).then((taskId: number) => {
        commit('removeTask', {
          taskState: payload.taskStateFrom,
          taskId: taskId
        })
      })
    },
    async blacklistTaskAsync ({ commit, dispatch }: any, payload: { taskState: AsyncTaskStatus, task: { id: number, type: AsyncTaskType, updateDate: string }}) {
      await dispatch('addTaskAsync', {
        taskState: AsyncTaskStatus.BLACKLIST,
        task: { id: payload.task.id, type: payload.task.type, updateDate: payload.task.updateDate }
      }).then((taskId: number) => {
        commit('removeTask', {
          taskState: payload.taskState,
          taskId: taskId
        })
      })
    },
    async deleteExpiredTaskFromTaskList ({ dispatch }: any, payload: { tasks: any, state: AsyncTaskStatus }) {
      const currentDate = new Date().getTime()
      for (const task of payload.tasks) {
        const taskDate = new Date(task.updateDate + 'Z').getTime()
        if ((currentDate - taskDate) > (6 * TIME.MILLISECONDS_IN_AN_HOUR)) {
          await dispatch('removeTaskAsync', { taskState: payload.state, taskId: task.id })
        }
      }
    },
    async deleteExpiredStoredTasks ({ getters, dispatch }: any) {
      const doneTasks = getters.doneTasks
      await dispatch('deleteExpiredTaskFromTaskList', { tasks: doneTasks, state: AsyncTaskStatus.DONE })
      const errorTasks = getters.errorTasks
      await dispatch('deleteExpiredTaskFromTaskList', { tasks: errorTasks, state: AsyncTaskStatus.ERROR })
      const canceledTasks = getters.canceledTasks
      await dispatch('deleteExpiredTaskFromTaskList', { tasks: canceledTasks, state: AsyncTaskStatus.CANCELED })
      const blacklistTasks = getters.blacklistTasks
      await dispatch('deleteExpiredTaskFromTaskList', { tasks: blacklistTasks, state: AsyncTaskStatus.BLACKLIST })
    }
  }
}
